import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaDownload, FaArrowDown } from 'react-icons/fa';

const Hero = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const roles = [
    'AI & Data Science Engineer',
    'Software Developer',
    'App Developer',
    'MLOps Engineer'
  ];

  // Typing animation effect
  useEffect(() => {
    const currentRole = roles[roleIndex];
    const typingSpeed = isDeleting ? 30 : 65;
    const pauseTime = isDeleting ? 300 : 2000;

    if (!isDeleting && displayText === currentRole) {
      setTimeout(() => setIsDeleting(true), pauseTime);
      return;
    }

    if (isDeleting && displayText === '') {
      setIsDeleting(false);
      setRoleIndex((prev) => (prev + 1) % roles.length);
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayText(
        isDeleting
          ? currentRole.substring(0, displayText.length - 1)
          : currentRole.substring(0, displayText.length + 1)
      );
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, roleIndex]);

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.15,
        staggerChildren: 0.12
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1] // Custom snappy bezier reveal
      }
    }
  };

  // Helper for rendering reticle bracket corners on hover
  const renderBrackets = () => (
    <>
      <span className="reticle-bracket top-0 left-0 border-t border-l border-white-pure group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket top-0 right-0 border-t border-r border-white-pure group-hover:-translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 left-0 border-b border-l border-white-pure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 right-0 border-b border-r border-white-pure group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </>
  );

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-void"
    >
      {/* Precision CAD gridlines helper in background */}
      <div className="absolute inset-0 z-0 bg-cad-grid opacity-[0.2]" />

      {/* Content */}
      <motion.div
        className="relative z-10 container-custom section-padding text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="mb-6">
          <span className="inline-block px-3 py-1.5 border border-grey-800 bg-elevated text-[9px] font-mono font-semibold tracking-[0.3em] uppercase text-grey-400">
            SYSTEM // LOADED_OK
          </span>
        </motion.div>

        <motion.h1
          variants={itemVariants}
          className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-display font-black leading-none tracking-tighter mb-6"
        >
          <span className="block text-white-primary mb-1">
            Jerwin Titus D
          </span>
          <span className="block chrome-text uppercase tracking-tighter font-extrabold pb-3">
            AI Engineer
          </span>
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="text-base sm:text-lg md:text-xl font-mono text-grey-400 mb-8 h-8 flex items-center justify-center uppercase tracking-wider"
        >
          <span className="text-grey-400 mr-2">CURRENTLY:</span>
          <span className="text-white-pure font-bold">{displayText}</span>
          <span className="inline-block w-[3px] h-4 bg-white-pure ml-1 animate-pulse"></span>
        </motion.p>

        <motion.p
          variants={itemVariants}
          className="text-xs sm:text-sm text-grey-400 mb-12 max-w-lg mx-auto tracking-widest leading-relaxed uppercase font-mono"
        >
          Architecting real-time MLOps, deep learning inference layers, and high-performance CV pipelines.
          <span className="text-grey-600 block mt-3 font-semibold">REF // COIMBATORE, IND</span>
        </motion.p>

        {/* Action Buttons with corner reticles */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap items-center justify-center gap-4 mb-16"
        >
          <button
            onClick={scrollToProjects}
            className="btn-precision group relative"
          >
            {renderBrackets()}
            <span className="flex items-center gap-2">
              View Deployments
              <FaArrowDown className="text-xs" />
            </span>
          </button>

          <a
            href="https://github.com/JerwinTitus2006"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-precision-outline group relative"
          >
            {renderBrackets()}
            <span className="flex items-center gap-2">
              <FaGithub className="text-sm" />
              Developer Node
            </span>
          </a>

          <a
            href="/RESUME JERWIN.pdf"
            download="Jerwin_Titus_Resume.pdf"
            className="btn-precision-outline group relative"
          >
            {renderBrackets()}
            <span className="flex items-center gap-2">
              <FaDownload className="text-xs" />
              Resume System
            </span>
          </a>
        </motion.div>

        {/* Scroll down indicator styled as a vertical CAD ticking path */}
        <motion.div
          variants={itemVariants}
          className="flex justify-center"
        >
          <motion.div
            className="flex flex-col items-center cursor-pointer group"
            onClick={scrollToProjects}
          >
            <span className="text-[8px] font-mono tracking-[0.4em] uppercase text-grey-400 group-hover:text-white-pure transition-colors duration-200 mb-2">SCROLL_SYSTEM</span>
            <div className="w-[1px] h-12 bg-grey-800 relative overflow-hidden">
              <motion.div 
                className="absolute top-0 left-0 right-0 h-4 bg-white-pure"
                animate={{ y: [0, 48, 0] }}
                transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
              />
            </div>
            <div className="w-1.5 h-1.5 bg-grey-600 mt-1" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
